import { GitMerge, Mail, Smartphone, StickyNote, Tag } from "lucide-react"

import { ActionDefinition } from "@/components/automations/types"

export const contactActions: ActionDefinition[] = [
  {
    key: "action__contact_add_tag",
    name: "Add tag",
    description: "Add a tag to the contact",
    category: "contact",
    nodeType: "action",
    recordType: "contact",
    icon: Tag,
    requiresSubjects: ["contact"],
    fields: [
      {
        key: "tag_id",
        label: "Tag",
        inputType: "select",
        required: true,
        optionsSource: "tags",
      },
    ],
  },
  {
    key: "action__contact_remove_tag",
    name: "Remove tag",
    description: "Remove a tag from the contact",
    category: "contact",
    nodeType: "action",
    recordType: "contact",
    icon: Tag,
    requiresSubjects: ["contact"],
    fields: [
      {
        key: "tag_id",
        label: "Tag",
        inputType: "select",
        required: true,
        optionsSource: "tags",
      },
    ],
  },
  {
    key: "action__contact_add_note",
    name: "Add note",
    description: "Add a note to the contact record",
    category: "contact",
    nodeType: "action",
    recordType: "contact",
    icon: StickyNote,
    requiresSubjects: ["contact"],
    fields: [
      {
        key: "content",
        label: "Note content",
        inputType: "markdown",
        required: true,
        supportsInterpolation: true,
      },
      {
        key: "tag_id",
        label: "Label",
        inputType: "select",
        required: false,
        optionsSource: "note_tags",
      },
    ],
  },
  {
    key: "action__contact_add_to_sequence",
    name: "Add to sequence",
    description: "Add the contact to a sequence",
    category: "contact",
    nodeType: "action",
    recordType: "contact",
    icon: GitMerge,
    requiresSubjects: ["contact"],
    fields: [
      {
        key: "sequence_id",
        label: "Sequence",
        inputType: "select",
        required: true,
        optionsSource: "sequences",
      },
      {
        key: "recipients",
        label: "Recipients",
        inputType: "email_recipients",
        required: true,
        defaultValue: { person: "to" },
        chatValueShape: '{"person":"to|cc|bcc|none"}',
      },
    ],
    chatFields: [
      {
        key: "team_member_recipients",
        label: "Additional team members",
        inputType: "json",
        required: false,
        optionsSource: "users",
        chatValueShape: '{"<user id>":"to|cc|bcc|none"}',
      },
    ],
  },
  {
    key: "action__contact_remove_from_sequence",
    name: "Remove from sequence",
    description: "Remove the contact from a sequence",
    category: "contact",
    nodeType: "action",
    recordType: "contact",
    icon: GitMerge,
    requiresSubjects: ["contact"],
    fields: [
      {
        key: "sequence_id",
        label: "Sequence",
        inputType: "select",
        required: true,
        optionsSource: "sequences",
        options: [{ value: "all", label: "All sequences" }],
        helperText: "Select a specific sequence or remove from all",
      },
    ],
  },
  {
    key: "action__contact_send_email",
    name: "Send email",
    description: "Send an email to the contact",
    category: "contact",
    nodeType: "action",
    recordType: "contact",
    icon: Mail,
    requiresSubjects: ["contact"],
    singlePerBranch: true,
    fields: [
      {
        key: "email_integration_id",
        label: "Send from",
        inputType: "email_integration",
        required: true,
        optionsSource: "accessible_email_integrations",
      },
      {
        key: "recipients",
        label: "Recipients",
        inputType: "email_recipients",
        required: true,
        defaultValue: { person: "to" },
        chatValueShape: '{"person":"to|cc|bcc|none"}',
      },
      {
        key: "email_content",
        label: "Email content",
        inputType: "email_content",
        required: true,
        defaultValue: {
          email_template_id: null,
          email_template_name: null,
          subject: "",
          body: { html: "" },
        },
        optionsSource: "email_templates",
        chatValueShape:
          'Either {"email_template_id":"<id>","email_template_name":"<name>","subject":"","body":{"html":""}} or {"email_template_id":null,"subject":"...","body":{"html":"..."}}',
        supportsInterpolation: true,
        interpolationHelperText: "Subject and body support variables from subject data and prior step results.",
      },
    ],
    chatFields: [
      {
        key: "cc_user_or_team_ids",
        label: "Additional internal recipients",
        inputType: "json",
        required: false,
        optionsSource: "assignments",
        chatValueShape:
          '{"user_ids":["<id after user: prefix>"],"team_ids":["<id after team: prefix>"],"type":"cc|bcc|none"}',
      },
    ],
  },
  {
    key: "action__contact_send_sms",
    name: "Send SMS",
    description: "Send a text message to the contact",
    category: "contact",
    nodeType: "action",
    recordType: "contact",
    icon: Smartphone,
    requiresSubjects: ["contact"],
    singlePerBranch: true,
    fields: [
      {
        key: "sms_integration",
        label: "Send from",
        inputType: "sms_integration",
        required: true,
        optionsSource: "phone_integrations",
      },
      {
        key: "recipients",
        label: "Recipients",
        inputType: "sms_recipients",
        required: true,
        defaultValue: { person: true },
        chatValueShape: '{"person":true|false}',
      },
      {
        key: "message",
        label: "Message",
        inputType: "sms_message",
        required: true,
        placeholder: "Hi {{person.preferred_name}}, ...",
        helperText: "Supports variables like {{person.preferred_name}}",
        rows: 8,
        supportsInterpolation: true,
      },
    ],
  },
]
